import { Fragment, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Button, Dialog, DialogTitle, Stack, Typography } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { Group } from '../types/models'

interface Props {
  group: Group
}

/**
 * Error codes (also used by `DELETE /user-groups/:id`) explaining why the group cannot be deleted.
 * Admins do not block deletion; their roles go with the group. `child_count` counts sub-organisations
 * (also hidden ones); `default_group_count` counts users' permanent default groups, which are never
 * shown in the sub-group list.
 */
export function deleteGroupBlockers(group: Group): { code: string, count?: number }[] {
  const blockers: { code: string, count?: number }[] = []
  if (group.permanent) {
    blockers.push({ code: 'USER_GROUP_PERMANENT' })
  }
  if (group.user_count) {
    blockers.push({ code: 'USER_GROUP_HAS_USERS', count: group.user_count })
  }
  if (group.child_count) {
    blockers.push({ code: 'USER_GROUP_HAS_CHILDREN', count: group.child_count })
  }
  if (group.default_group_count) {
    blockers.push({ code: 'USER_GROUP_HAS_DEFAULT_GROUPS', count: group.default_group_count })
  }
  return blockers
}

/**
 * Delete button for an administrable sub-group, with a confirmation dialog. Disabled, with the
 * reason shown, when the server would refuse the deletion.
 */
export function DeleteGroupButton({ group }: Props): React.ReactElement | null {
  const [confirm, setConfirm] = useState(false)
  const { t } = useTranslation()
  const app = useApp()
  const navigate = useNavigate()

  if (!group.id || !group.editable || !group.parent_group_id) {
    return null
  }

  const blockers = deleteGroupBlockers(group)

  const onDelete = () => {
    callAxios({ method: 'DELETE', url: `/user-groups/${group.id}` })
      .then(res => {
        setConfirm(false)
        if (res.success) {
          app.addInfo(t('Group deleted.'))
          navigate(`/admin/user-groups/${group.parent_group_id}`, { replace: true })
        } else {
          app.addError(res)
        }
      })
  }

  return (
    <Fragment>
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Button data-cy="button-Delete group" variant="outlined" color="error" size="large"
          disabled={blockers.length > 0} onClick={() => setConfirm(true)} endIcon={<Delete/>}>
          <Trans>Delete group</Trans>
        </Button>
        {blockers.map(b =>
          <Typography key={b.code} data-cy={`delete-group-blocker-${b.code}`} variant="body2" sx={{ mt: 1 }}>
            {t(b.code).replace('{count}', String(b.count ?? ''))}
          </Typography>
        )}
      </Box>

      <Dialog open={confirm} onClose={() => setConfirm(false)}>
        <DialogTitle sx={{ backgroundColor: 'error.light' }}>
          {t('Delete group {name}?').replace('{name}', group.name)}
        </DialogTitle>
        <Box sx={{ p: 2 }}>
          <Typography><Trans>Delete group confirm</Trans></Typography>
          <Stack direction="row" spacing={2} sx={{ m: 2 }}>
            <Button color="primary" onClick={() => setConfirm(false)}><Trans>Cancel</Trans></Button>
            <Button color="secondary" data-cy="button-Delete group OK" onClick={onDelete}><Trans>OK</Trans></Button>
          </Stack>
        </Box>
      </Dialog>
    </Fragment>
  )
}
